import { ArtifactType } from "../../types/artifact";
import { RiskAnalysisContent } from "../../types/risk-analysis";
import { logError, normalizeError } from "../../utils/error";
import { normalizeRiskAnalysisContent } from "../../utils/riskAnalysis";
import { supabase } from "../supabase/client";

export interface SummaryContent {
  usage: {
    duration: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens: number;
    cache_creation_input_tokens: number;
  };
  intent: string;
  summary: string;
  category: string;
  duration: number;
  language: string;
  next_step: {
    timeframe: string;
    description: string;
  };
  relevance: "high" | "medium" | "low";
  sentiment: "Positive" | "Neutral" | "Negative";
  apiSummary: {
    header: string;
    outcome: string;
  };
  disposition: string;
  assignedUser: {
    email: string;
    mobile: string;
    userId: string;
    manager: string | null;
    jobTitle: string;
    lastName: string;
    firstName: string;
  } | null;
  callAnalysis: any | null;
  durationText: string;
  participants: Array<{
    name: string;
    role: string;
    company: string;
    contact_number: string;
    crm_identifier: string;
  }>;
  assignedContact: any | null;
  extendedSummary: string;
  connectedParties: Array<{
    id: string;
    type: string;
    email: string;
    timezone: string;
    isInternal: boolean;
    displayValue: string;
  }>;
  accountHasSpokeCredentials?: boolean;
  interactionId?: string;
}

export interface ArtifactResponse {
  content: SummaryContent | any;
  type: string;
  modified: string;
}

export interface FetchArtifactsForViewerParams {
  tenantId: string;
  interactionId: string;
}

export interface ViewerArtifacts {
  summary?: ArtifactResponse | null;
  scorecard?: ArtifactResponse | null;
  riskAnalysis?: ArtifactResponse | null;
  riskAnalysisNormalized?: RiskAnalysisContent;
  transcript?: ArtifactResponse | null;
  callDataRecord?: ArtifactResponse | null;
  callRecordingEvent?: ArtifactResponse | null;
  callTranscriptionRecord?: ArtifactResponse | null;
  preProcessor?: ArtifactResponse | null;
  protocolRouter?: ArtifactResponse | null;
  all: ArtifactResponse[];
}

const artifactTypeMap: Record<string, keyof Omit<ViewerArtifacts, "all">> = {
  [ArtifactType.Summary]: "summary",
  [ArtifactType.Scorecard]: "scorecard",
  [ArtifactType.RiskAnalysis]: "riskAnalysis",
  [ArtifactType.Transcript]: "transcript",
  [ArtifactType.CallDataRecord]: "callDataRecord",
  [ArtifactType.CallTranscriptionRecord]: "callTranscriptionRecord",
  [ArtifactType.CallRecordingEvent]: "callRecordingEvent",
  [ArtifactType.PreProcessor]: "preProcessor",
  [ArtifactType.ProtocolRouter]: "protocolRouter"
};

const genArtifactMapFromData = (data: ArtifactResponse[]) => {
  // we want a full set even if some of the input is null
  const result: ViewerArtifacts = {
    summary: null,
    scorecard: null,
    riskAnalysis: null,
    riskAnalysisNormalized: undefined,
    transcript: null,
    callDataRecord: null,
    callRecordingEvent: null,
    callTranscriptionRecord: null,
    preProcessor: null,
    protocolRouter: null,
    all: []
  };

  const mapped = data.reduce<ViewerArtifacts>((acc, artifact) => {
    const propertyName = artifactTypeMap[artifact.type];

    return {
      ...acc,
      all: [...acc.all, artifact],
      ...(propertyName ? { [propertyName]: artifact } : {})
    };
  }, result);

  if (mapped.riskAnalysis?.content?.riskAnalysis) {
    mapped.riskAnalysisNormalized = normalizeRiskAnalysisContent(
      mapped.riskAnalysis.content.riskAnalysis
    );
  }

  return mapped;
};

/**
 * Fetches all artifacts for a specific interaction
 * @param interactionId - The ID of the interaction
 * @param tenantId - The ID of the tenant
 * @returns An object containing all artifacts for the interaction
 */
export const fetchArtifactsForViewer = async (
  params: FetchArtifactsForViewerParams
): Promise<{ data: ViewerArtifacts | null; error: Error | null }> => {
  try {
    const { tenantId, interactionId } = params;

    const { data, error } = await supabase
      .from("artifact")
      .select("content, type, modified, interaction_id, tenant_id")
      .eq("interaction_id", interactionId)
      .eq("tenant_id", tenantId);

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      return { data: null, error: new Error("No artifacts found") };
    }

    const mappedArtifacts = genArtifactMapFromData(data);

    const { data: accountData, error: accountError } = await supabase
      .from("account")
      .select("spoke_client_id, spoke_client_secret")
      .eq("spoke_tenant_id", tenantId)
      .single();

    if (accountError || !accountData) {
      return { data: mappedArtifacts, error: null };
    }

    const hasSpokeCredentials = Boolean(accountData.spoke_client_id && accountData.spoke_client_secret);

    if (mappedArtifacts.summary?.content) {
      mappedArtifacts.summary.content.accountHasSpokeCredentials = hasSpokeCredentials;
      mappedArtifacts.summary.content.interactionId = interactionId;
    }

    return { data: mappedArtifacts, error: null };
  } catch (error) {
    logError("Error fetching artifacts", error);

    return {
      data: null,
      error: normalizeError(error)
    };
  }
};

/**
 * Fetches a specific artifact type for an interaction
 * @param interactionId - The ID of the interaction
 * @param tenantId - The ID of the tenant
 * @param type - The type of artifact to fetch
 * @returns The artifact data or null if not found
 */
export const fetchArtifactByType = async (
  interactionId: string,
  type: ArtifactType
): Promise<{ data: ArtifactResponse | null; error: Error | null }> => {
  try {
    const { data, error } = await supabase
      .from("artifact")
      .select("content, type, modified")
      .eq("interaction_id", interactionId)
      .eq("type", type)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No rows returned
        return { data: null, error: null };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    logError(`Error fetching ${type} artifact`, error);
    return {
      data: null,
      error: normalizeError(error)
    };
  }
};
